'use client'

import {
  BeautyFrame,
  BeautyKicker,
  BeautyNumber,
  FullBleedImage,
  SafeText,
  beautyCream,
  beautyInk,
  beautyIvory,
  beautyMuted,
  beautySans,
  beautySerif,
  clampIndex,
  getBeautyCopy,
  getBeautyDesign,
  getBeautyVisual,
  type BeautyPackProps,
} from '../shared/BeautyPackCore'

import {
  CalendarDays,
  CheckCircle2,
  Droplets,
  Heart,
  Leaf,
  ShieldCheck,
  Sparkles,
  SunMedium,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react'

const bg = [
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/announcement_01.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/announcement_02.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/announcement_03.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/announcement_04.jpg',
  'https://hubee.app/images/creator-studio/publications/carousel_templates/beauty_01/announcement_05.jpg',
]

const total = 5

const fallbacks = [
  {
    eyebrow: 'Nuevo',
    headline: 'Llega lo que tu piel estaba esperando',
    subheadline:
      'Presentamos una nueva propuesta de skincare diseñada para una rutina más luminosa, ligera y sofisticada.',
    cta: 'Guarda este anuncio',
    bullets: ['Ingredientes limpios', 'Textura ligera', 'Glow saludable'],
  },
  {
    eyebrow: 'Qué la hace especial',
    headline: 'Una fórmula pensada para sentirse tan bien como se ve.',
    subheadline:
      'Combina sensorialidad, eficacia y una experiencia más elegante para tu rutina de todos los días.',
    bullets: [
      'Ingredientes naturales y fórmulas limpias',
      'Resultados visibles desde los primeros días',
      'Texturas ligeras para uso diario',
    ],
  },
  {
    eyebrow: 'Ciencia + naturaleza',
    headline: 'Activos eficaces, botánicos inteligentes.',
    subheadline:
      'Una mezcla equilibrada entre ciencia cosmética y extractos que acompañan la salud de tu piel.',
    body: 'Sin parabenos · Sin fragancias agresivas · Sin crueldad animal',
    bullets: ['Sin parabenos', 'Sin fragancias sintéticas', 'Sin crueldad animal'],
  },
  {
    eyebrow: 'Tu rutina elevada',
    headline: 'Menos pasos. Más intención.',
    subheadline:
      'Una secuencia simple y bien pensada para que tu piel se vea equilibrada, luminosa y cuidada.',
    steps: [
      'Sérum — hidrata y fortalece.',
      'Crema — nutre y restaura.',
      'Protector — protege y previene.',
    ],
  },
  {
    eyebrow: 'Tu mejor piel empieza hoy',
    headline: 'Haz espacio para un ritual que sí quieras repetir.',
    subheadline:
      'Descubre una nueva línea de skincare pensada para elevar tu rutina con suavidad, equilibrio y resultados reales.',
    result: 'Guarda este anuncio y compártelo con quien necesite renovar su rutina.',
    cta: 'Descubrir lanzamiento',
  },
]

function splitHeadlineForAccent(text: string) {
  const clean = String(text || '').trim()
  if (!clean) return { main: '', accent: '' }

  const words = clean.split(/\s+/).filter(Boolean)

  if (words.length <= 3) {
    return {
      main: words.slice(0, 1).join(' '),
      accent: words.slice(1).join(' '),
    }
  }

  return {
    main: words.slice(0, -2).join(' '),
    accent: words.slice(-2).join(' '),
  }
}

function renderAccentHeadline(text: string, accentColor: string) {
  const { main, accent } = splitHeadlineForAccent(text)

  return (
    <>
      {main ? <span>{main}</span> : null}
      {main && accent ? <br /> : null}
      {accent ? <span style={{ color: accentColor }}>{accent}</span> : null}
    </>
  )
}

type AnnouncementBullet = string | {
  title?: string
  text?: string
  body?: string
  label?: string
  iconKey?: string
}

const announcementIconMap: Record<string, LucideIcon> = {
  leaf: Leaf,
  natural: Leaf,
  botanical: Leaf,
  ingredient: Leaf,

  droplets: Droplets,
  hydration: Droplets,
  moisture: Droplets,
  water: Droplets,

  shield: ShieldCheck,
  protection: ShieldCheck,
  spf: SunMedium,
  sun: SunMedium,

  sparkle: Sparkles,
  glow: Sparkles,
  luminosity: Sparkles,
  bright: Sparkles,

  calendar: CalendarDays,
  routine: CalendarDays,
  daily: CalendarDays,

  heart: Heart,
  calm: Heart,
  sensitive: Heart,
  gentle: Heart,

  check: CheckCircle2,
  result: CheckCircle2,
  visible: CheckCircle2,

  wand: WandSparkles,
  premium: WandSparkles,
  beauty: WandSparkles,
}

function getBulletText(item: AnnouncementBullet) {
  if (typeof item === 'string') return item

  return String(
    item?.title ||
      item?.text ||
      item?.body ||
      item?.label ||
      ''
  ).trim()
}

function pickAnnouncementIcon(item: AnnouncementBullet): LucideIcon {
  if (item && typeof item === 'object' && item.iconKey) {
    const key = String(item.iconKey).toLowerCase().trim()
    return announcementIconMap[key] || Sparkles
  }

  const value = getBulletText(item).toLowerCase()

  if (
    value.includes('natural') ||
    value.includes('botánico') ||
    value.includes('botanico') ||
    value.includes('ingrediente') ||
    value.includes('limpio') ||
    value.includes('clean')
  ) {
    return Leaf
  }

  if (
    value.includes('hidrata') ||
    value.includes('hidrat') ||
    value.includes('humedad') ||
    value.includes('moisture') ||
    value.includes('agua') ||
    value.includes('water')
  ) {
    return Droplets
  }

  if (
    value.includes('protege') ||
    value.includes('protección') ||
    value.includes('proteccion') ||
    value.includes('spf') ||
    value.includes('solar') ||
    value.includes('escudo')
  ) {
    return SunMedium
  }

  if (
    value.includes('glow') ||
    value.includes('luminos') ||
    value.includes('brillo') ||
    value.includes('radiante') ||
    value.includes('bright')
  ) {
    return Sparkles
  }

  if (
    value.includes('diario') ||
    value.includes('rutina') ||
    value.includes('constancia') ||
    value.includes('daily')
  ) {
    return CalendarDays
  }

  if (
    value.includes('suave') ||
    value.includes('calma') ||
    value.includes('sensible') ||
    value.includes('gentle')
  ) {
    return Heart
  }

  if (
    value.includes('resultado') ||
    value.includes('visible') ||
    value.includes('eficaz') ||
    value.includes('effective')
  ) {
    return CheckCircle2
  }

  return Sparkles
}

export default function BeautyAnnouncementPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: BeautyPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getBeautyDesign(draft, '#8D8A3B')
  const accent = design.accent || '#8D8A3B'
  const textMuted = design.textMuted || beautyMuted

  const copy = getBeautyCopy(draft, index, fallbacks[index])
  const bullets = (copy.bullets?.length ? copy.bullets : fallbacks[index].bullets || []) as AnnouncementBullet[]
  const steps = copy.steps?.length ? copy.steps : fallbacks[index].steps || []

  return (
    <BeautyFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      showControls={showControls}
      nav={index === 0 || index === 4 ? 'light' : 'dark'}
      className="bg-[#F6EEE3]"
      borderRadius={exportMode ? 0 : 34}
    >
      {index === 0 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[0], {
              overlay: 'white',
              opacity: 0.08,
            })}
            objectPosition="78% center"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#F6EEE3]/96 via-[#F6EEE3]/78 to-[#F6EEE3]/10" />

          <div className="absolute left-8 top-10 z-10 max-w-[58%]">
            <SafeText
              className="text-[12px] font-bold uppercase tracking-[0.24em]"
              style={{ fontFamily: beautySans, color: beautyInk }}
            >
              {copy.eyebrow || 'Nuevo'}
            </SafeText>

            <div className="mt-3 h-px w-[112px]" style={{ background: `${accent}88` }} />
          </div>

          <div className="absolute left-8 top-[23%] z-10 max-w-[56%]">
            <SafeText
              className="text-[48px] font-normal leading-[0.88] tracking-[-0.055em]"
              style={{ fontFamily: beautySerif, color: beautyInk }}
            >
              {renderAccentHeadline(copy.headline, accent)}
            </SafeText>

            <div className="mt-7 h-px w-[54px]" style={{ background: `${accent}88` }} />

            <SafeText
              className="mt-6 max-w-[92%] text-[19px] leading-[1.42]"
              style={{ fontFamily: beautySans, color: '#3A302B' }}
            >
              {copy.subheadline}
            </SafeText>
          </div>

          <div className="absolute bottom-11 left-8 z-10">
            <div
              className="inline-flex items-center gap-3 rounded-full px-5 py-3 text-[11px] font-bold uppercase tracking-[0.16em]"
              style={{
                background: 'rgba(255,255,255,0.76)',
                color: beautyInk,
                border: `1px solid ${accent}33`,
                fontFamily: beautySans,
              }}
            >
              <span
                className="inline-block h-4 w-4 rounded-sm border"
                style={{ borderColor: `${beautyInk}66` }}
              />
              {copy.cta || 'Guarda este anuncio'}
            </div>
          </div>

          <div
            className="absolute bottom-10 right-8 z-10 text-[32px] font-normal tracking-[-0.04em]"
            style={{ fontFamily: beautySerif, color: accent }}
          >
            01
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <div className="relative h-full overflow-hidden" style={{ background: beautyIvory }}>
          <div className="absolute left-8 top-8 right-8 z-10 flex items-start justify-between">
            <BeautyNumber value="02" color={accent} />
            <div className="ml-5 mt-3 h-px flex-1" style={{ background: 'rgba(33,24,21,0.18)' }} />
          </div>

          <div className="absolute left-8 top-[14%] z-10 max-w-[50%]">
            <SafeText
              className="text-[14px] font-bold uppercase tracking-[0.18em]"
              style={{ fontFamily: beautySans, color: beautyInk }}
            >
              {copy.eyebrow || 'Qué la hace especial'}
            </SafeText>

            <SafeText
              className="mt-7 text-[40px] font-normal leading-[0.9] tracking-[-0.05em]"
              style={{ fontFamily: beautySerif, color: beautyInk }}
            >
              {copy.headline}
            </SafeText>

            <SafeText
              className="mt-7 max-w-[94%] text-[18px] leading-[1.48]"
              style={{ fontFamily: beautySans, color: '#4B4039' }}
            >
              {copy.subheadline}
            </SafeText>
          </div>

          <div className="absolute right-0 top-0 h-full w-[40%] overflow-hidden">
            <FullBleedImage
              {...getBeautyVisual(draft, index, bg[1], {
                overlay: 'white',
                opacity: 0.1,
              })}
              objectPosition="center"
            />
          </div>

          <div className="absolute left-8 bottom-16 z-10 flex max-w-[53%] flex-col gap-4">
            {bullets.slice(0, 3).map((item, idx) => {
              const Icon = pickAnnouncementIcon(item)
              const text = getBulletText(item)

              return (
                <div key={`${idx}-${text}`} className="flex items-center gap-4">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border"
                    style={{
                      borderColor: `${accent}55`,
                      color: accent,
                      background: '#F3EBDD',
                    }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </div>

                  <SafeText
                    className="flex-1 text-[16px] leading-[1.42]"
                    style={{ fontFamily: beautySans, color: '#3E342E' }}
                  >
                    {text}
                  </SafeText>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {index === 2 ? (
        <div className="relative h-full overflow-hidden" style={{ background: beautyCream }}>
          <div className="absolute left-8 top-8 right-8 z-10 flex items-start justify-between">
            <BeautyNumber value="03" color={accent} />
            <div className="ml-5 mt-3 h-px flex-1" style={{ background: 'rgba(33,24,21,0.18)' }} />
          </div>

          <div className="absolute left-8 top-[15%] z-10 max-w-[48%]">
            <SafeText
              className="text-[14px] font-bold uppercase tracking-[0.18em]"
              style={{ fontFamily: beautySans, color: beautyInk }}
            >
              {copy.eyebrow || 'Ciencia + naturaleza'}
            </SafeText>

            <SafeText
              className="mt-6 text-[40px] font-normal leading-[0.92] tracking-[-0.05em]"
              style={{ fontFamily: beautySerif, color: beautyInk }}
            >
              {copy.headline}
            </SafeText>

            <SafeText
              className="mt-6 max-w-[94%] text-[17px] leading-[1.48]"
              style={{ fontFamily: beautySans, color: '#463A34' }}
            >
              {copy.subheadline}
            </SafeText>

            <div
              className="mt-9 rounded-[22px] px-5 py-5"
              style={{ background: '#E9DDCC' }}
            >
              <SafeText
                className="text-[14px] leading-[1.7]"
                style={{ fontFamily: beautySans, color: beautyInk }}
              >
                {(bullets.slice(0, 3).length ? bullets.slice(0, 3).join('\n') : copy.body)
                  .split('\n')
                  .map((line, idx) => (
                    <div key={idx}>{line}</div>
                  ))}
              </SafeText>
            </div>
          </div>

          <div className="absolute right-0 top-0 h-full w-[42%] overflow-hidden">
            <FullBleedImage
              {...getBeautyVisual(draft, index, bg[2], {
                overlay: 'white',
                opacity: 0.06,
              })}
              objectPosition="72% center"
            />
          </div>
        </div>
      ) : null}

      {index === 3 ? (
        <div className="relative h-full overflow-hidden" style={{ background: beautyIvory }}>
          <div className="absolute left-8 top-8 right-8 z-10 flex items-start justify-between">
            <BeautyNumber value="04" color={accent} />
            <div className="ml-5 mt-3 h-px flex-1" style={{ background: 'rgba(33,24,21,0.18)' }} />
          </div>

          <div className="absolute right-0 top-0 h-full w-[42%] overflow-hidden">
            <FullBleedImage
              {...getBeautyVisual(draft, index, bg[3], {
                overlay: 'white',
                opacity: 0.08,
              })}
              objectPosition="72% center"
            />
          </div>

          <div className="absolute left-8 top-[15%] z-10 max-w-[48%]">
            <SafeText
              className="text-[14px] font-bold uppercase tracking-[0.18em]"
              style={{ fontFamily: beautySans, color: beautyInk }}
            >
              {copy.eyebrow || 'Tu rutina, elevada'}
            </SafeText>

            <SafeText
              className="mt-6 text-[40px] font-normal leading-[0.92] tracking-[-0.05em]"
              style={{ fontFamily: beautySerif, color: beautyInk }}
            >
              {copy.headline}
            </SafeText>

            <SafeText
              className="mt-6 text-[17px] leading-[1.48]"
              style={{ fontFamily: beautySans, color: '#463A34' }}
            >
              {copy.subheadline}
            </SafeText>

            <div className="mt-9 flex flex-col gap-4">
              {steps.slice(0, 3).map((item, idx) => (
                <div key={`${idx}-${item}`} className="flex items-center gap-4">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-[15px] font-semibold"
                    style={{
                      borderColor: `${accent}66`,
                      color: beautyInk,
                      background: 'rgba(255,255,255,0.72)',
                      fontFamily: beautySans,
                    }}
                  >
                    {idx + 1}
                  </div>

                  <SafeText
                    className="flex-1 text-[15px] leading-[1.42]"
                    style={{ fontFamily: beautySans, color: beautyInk }}
                  >
                    {item}
                  </SafeText>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {index === 4 ? (
        <>
          <FullBleedImage
            {...getBeautyVisual(draft, index, bg[4], {
              overlay: 'white',
              opacity: 0.1,
            })}
            objectPosition="74% center"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#F6EEE3]/94 via-[#F6EEE3]/74 to-[#F6EEE3]/18" />

          <div className="absolute left-8 top-8 right-8 z-10 flex items-start justify-between">
            <BeautyNumber value="05" color={accent} />
            <div className="ml-5 mt-3 h-px flex-1" style={{ background: 'rgba(33,24,21,0.18)' }} />
          </div>

          <div className="absolute left-8 top-[18%] z-10 max-w-[55%]">
            <SafeText
              className="text-[44px] font-normal leading-[0.9] tracking-[-0.055em]"
              style={{ fontFamily: beautySerif, color: beautyInk }}
            >
              {renderAccentHeadline(copy.headline, accent)}
            </SafeText>

            <div className="mt-6 h-px w-[52px]" style={{ background: `${accent}88` }} />

            <SafeText
              className="mt-6 max-w-[96%] text-[18px] leading-[1.48]"
              style={{ fontFamily: beautySans, color: '#433730' }}
            >
              {copy.subheadline}
            </SafeText>

            <SafeText
              className="mt-7 max-w-[95%] text-[15px] leading-[1.52]"
              style={{ fontFamily: beautySans, color: textMuted }}
            >
              {copy.result}
            </SafeText>

            <div className="mt-9 flex flex-col gap-3">
              <div
                className="inline-flex w-fit items-center rounded-full px-6 py-4 text-[12px] font-bold uppercase tracking-[0.16em] text-white"
                style={{ background: accent, fontFamily: beautySans }}
              >
                {copy.cta || 'Descubrir lanzamiento'}
              </div>

              <SafeText
                className="text-[12px] font-bold uppercase tracking-[0.16em]"
                style={{ fontFamily: beautySans, color: beautyInk }}
              >
                Comparte con quien lo necesite
              </SafeText>
            </div>
          </div>
        </>
      ) : null}
    </BeautyFrame>
  )
}